// @ts-check
// Rotas REST das conversas, arquivos e canais (docs/arquitetura-appcrm/06-contrato-conversas.md, seção 5).
import { api } from './api-client.js';
import { formatFullName } from '../lib/field-metadata.js';
import { serializeRestFilter } from '../lib/rest-filter.js';

/**
 * @typedef {{ handle: string, displayName?: string | null }} ConversationContact
 * @typedef {{ id: string, direction: 'INCOMING' | 'OUTGOING', text?: string | null, sentAt: string, kind?: string | null, status?: string | null }} LastMessage
 * @typedef {{ id: string, channelId: string, channelType: 'WHATSAPP' | 'EMAIL', subject?: string | null, externalThreadKey?: string,
 *   contact: ConversationContact, person: { id: string, name: any, avatarUrl?: string | null } | null,
 *   households: { id: string, name: string }[], enrollmentOpportunities: { id: string, name: string, stage: string }[],
 *   lastMessage: LastMessage | null, lastIncomingAt?: string | null, isServiceWindowOpen?: boolean, serviceWindowExpiresAt?: string | null,
 *   unreadCount: number, messageCount: number, createdAt: string, updatedAt: string, isAwaitingReply?: boolean, awaitingReplySince?: string | null,
 *   assignee?: ThreadAssignee | null, provisionalEnrollmentOpportunityId?: string | null, canMergeContactPerson?: boolean,
 *   notLead?: ThreadNotLead | null, isBlocked?: boolean, purpose?: string | null, botState?: string | null, handoffAt?: string | null,
 *   bot?: { botId: string, name: string, runId: string, startedAt: string } | null, isLastMessageRestricted?: boolean }} ConversationThread
 * @typedef {{ reason: string | null, detail: string | null, enrollmentOpportunityId: string | null }} ThreadNotLead
 * @typedef {{ notLeadReason: string, notLeadReasonDetail?: string | null, personRole?: string | null }} NotLeadThreadInput
 * @typedef {{ workspaceMemberId: string, name: string }} ThreadAssignee
 * @typedef {{ fileId: string, fileName: string, mimeType: string, size: number, kind: 'IMAGE' | 'AUDIO' | 'VIDEO' | 'DOCUMENT' | 'STICKER',
 *   isVoiceNote?: boolean, url: string }} MessageAttachment
 * @typedef {{ handle: string, displayName?: string | null, role?: 'TO' | 'CC' | 'BCC' }} MessageParticipant
 * @typedef {{ id: string, threadId: string, externalId?: string | null, direction: 'INCOMING' | 'OUTGOING',
 *   status: 'RECEIVED' | 'PENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED', sentAt: string, subject?: string | null,
 *   text?: string | null, html?: string | null, from: MessageParticipant, to: MessageParticipant[], attachments: MessageAttachment[],
 *   origin?: string, replyToMessageId?: string | null, reaction?: { emoji: string | null } | null, errorMessage?: string | null, sentBy?: { workspaceMemberId: string, name: string } | null,
 *   kind?: string, isOptimistic?: boolean }} ConversationMessage
 * @typedef {{ hasNextPage?: boolean, hasPreviousPage?: boolean, startCursor?: string | null, endCursor?: string | null }} PageInfo
 * @typedef {{ channelType?: string | null, personId?: string | null, householdId?: string | null, enrollmentOpportunityId?: string | null,
 *   searchInput?: string | null, unreadOnly?: boolean, awaitingReply?: boolean, withoutOpportunity?: boolean, assigneeWorkspaceMemberId?: string | null,
 *   limit?: number, startingAfter?: string | null, drilldown?: string | null,
 *   purpose?: 'CAPTURE' | 'SERVICE' | null, botState?: 'BOT' | 'HUMAN' | null }} ThreadListQuery
 *   drilldown: caixa aberta por um clique num gráfico de conversas (`s.<receita>`, contrato 07, seção 10)
 * @typedef {'MOTHER' | 'FATHER' | 'STEPMOTHER' | 'STEPFATHER' | 'GRANDMOTHER' | 'GRANDFATHER' | 'UNCLE_AUNT' | 'SIBLING' | 'LEGAL_GUARDIAN' | 'OTHER'} Kinship
 * @typedef {{ enrollmentOpportunityId: string, mergeIntoPersonId?: string | null, kinship?: Kinship | null }} MergeThreadInput
 * @typedef {{ id: string, name: string, role: 'POINT_OF_CONTACT' | 'HOUSEHOLD_MEMBER' | 'STUDENT', kinship: string | null }} OpportunityPerson
 * @typedef {{ id: string, type: 'WHATSAPP' | 'EMAIL', handle: string, displayName?: string | null, provider?: string,
 *   settings?: Record<string, any> | null, isActive?: boolean }} Channel
 */

/**
 * Lê `data.<chave>` com tolerância a respostas sem envelope.
 * @param {any} body
 * @param {string} key
 */
function readData(body, key) {
  const data = body?.data ?? body;
  if (data && typeof data === 'object' && key in data) return data[key];
  return data;
}

/**
 * @param {ThreadListQuery} query
 * @returns {Promise<{ threads: ConversationThread[], pageInfo: PageInfo }>}
 */
export async function listThreads({
  channelType, personId, householdId, enrollmentOpportunityId, searchInput, unreadOnly, awaitingReply, withoutOpportunity, assigneeWorkspaceMemberId, limit = 50, startingAfter,
  drilldown, purpose, botState,
} = {}) {
  const body = await api.get('/rest/conversations', {
    channelType: channelType ?? undefined,
    personId: personId ?? undefined,
    householdId: householdId ?? undefined,
    enrollmentOpportunityId: enrollmentOpportunityId ?? undefined,
    searchInput: searchInput?.trim() || undefined,
    unreadOnly: unreadOnly ? 'true' : undefined,
    awaitingReply: awaitingReply ? 'true' : undefined,
    withoutOpportunity: withoutOpportunity ? 'true' : undefined,
    assigneeWorkspaceMemberId: assigneeWorkspaceMemberId ?? undefined,
    limit,
    starting_after: startingAfter ?? undefined,
    drilldown: drilldown ?? undefined,
    // 3.0.0: finalidade e estado do bot (contrato 10 de atendimento, seção 21.2.1)
    purpose: purpose ?? undefined,
    botState: botState ?? undefined,
  });
  const threads = readData(body, 'threads');
  return { threads: Array.isArray(threads) ? threads : [], pageInfo: body?.pageInfo ?? { hasNextPage: false } };
}

/**
 * @param {string} threadId
 * @returns {Promise<ConversationThread>}
 */
export async function getThread(threadId) {
  const body = await api.get(`/rest/conversations/${threadId}`);
  return readData(body, 'thread');
}

/**
 * Membros que podem ser responsáveis pela conversa: os ativos que a leem pelas etapas do papel (contrato de equipe,
 * seção 3.2).
 * @param {string} threadId
 * @returns {Promise<Array<{ id: string, name: { firstName?: string, lastName?: string } | null }>>}
 */
export async function listAssignableThreadMembers(threadId) {
  const body = await api.get(`/rest/conversations/${threadId}/assignable-members`);
  const members = readData(body, 'members');
  return Array.isArray(members) ? members : [];
}

/**
 * Mensagens mais recentes por último; a paginação anda para trás.
 * @param {string} threadId
 * @param {{ limit?: number, startingBefore?: string | null }} [options]
 * @returns {Promise<{ messages: ConversationMessage[], pageInfo: PageInfo }>}
 */
export async function listMessages(threadId, { limit = 50, startingBefore } = {}) {
  const body = await api.get(`/rest/conversations/${threadId}/messages`, { limit, starting_before: startingBefore ?? undefined });
  const messages = readData(body, 'messages');
  return { messages: Array.isArray(messages) ? messages : [], pageInfo: body?.pageInfo ?? { hasPreviousPage: false } };
}

/**
 * @param {string} threadId
 * @param {{ text: string, fileIds?: string[], subject?: string }} input
 * @returns {Promise<ConversationMessage>}
 */
export async function sendMessage(threadId, input) {
  const body = await api.post(`/rest/conversations/${threadId}/messages`, input);
  return readData(body, 'message');
}

/**
 * Começa uma conversa: texto (e-mail, ou WhatsApp com a janela aberta) ou, no WhatsApp, um modelo aprovado com as
 * variáveis e o número dele (primeiro contato ou janela fechada).
 * @param {{ channelType: 'WHATSAPP' | 'EMAIL', to: string, subject?: string, text?: string, personId?: string | null, connectedAccountId?: string,
 *   fileIds?: string[], templateId?: string, variables?: Record<string, string>, channelId?: string }} input
 * @returns {Promise<{ thread: ConversationThread | null, message: ConversationMessage | null }>}
 */
export async function createConversation(input) {
  const body = await api.post('/rest/conversations', input);
  const data = body?.data ?? body ?? {};
  return { thread: data.thread ?? (data.channelType ? data : null), message: data.message ?? null };
}

/**
 * @param {string} threadId
 */
export async function markThreadRead(threadId) {
  const body = await api.post(`/rest/conversations/${threadId}/read`, {});
  return readData(body, 'thread');
}

/**
 * Vínculo manual; personId null desfaz o vínculo.
 * @param {string} threadId
 * @param {string | null} personId
 * @returns {Promise<ConversationThread>}
 */
export async function linkThreadToPerson(threadId, personId) {
  const body = await api.patch(`/rest/conversations/${threadId}`, { personId });
  return readData(body, 'thread');
}

/**
 * Responsável pela conversa; null deixa sem responsável.
 * @param {string} threadId
 * @param {string | null} workspaceMemberId
 * @returns {Promise<ConversationThread>}
 */
export async function assignThread(threadId, workspaceMemberId) {
  const body = await api.patch(`/rest/conversations/${threadId}`, { assigneeWorkspaceMemberId: workspaceMemberId });
  return readData(body, 'thread');
}

/**
 * Cria pessoa e família quando faltam e um card na primeira etapa do funil.
 * @param {string} threadId
 * @returns {Promise<{ thread: ConversationThread, enrollmentOpportunityId: string }>}
 */
export async function createOpportunityForThread(threadId) {
  const body = await api.post(`/rest/conversations/${threadId}/enrollment-opportunity`, {});
  const data = body?.data ?? body ?? {};
  return { thread: data.thread, enrollmentOpportunityId: data.enrollmentOpportunityId };
}

/**
 * @param {string} threadId
 * @param {MergeThreadInput} input
 * @returns {Promise<{ thread: ConversationThread, personId: string, householdId: string, enrollmentOpportunityId: string }>}
 */
export async function mergeThreadIntoOpportunity(threadId, input) {
  const body = await api.post(`/rest/conversations/${threadId}/merge`, {
    enrollmentOpportunityId: input.enrollmentOpportunityId,
    mergeIntoPersonId: input.mergeIntoPersonId ?? null,
    kinship: input.kinship ?? null,
  });
  return body?.data ?? body;
}

/**
 * "Não é lead" pela conversa: classifica o card provisório ou, sem card, só a conversa (contrato 06, seção 8.1).
 * @param {string} threadId
 * @param {NotLeadThreadInput} input
 * @returns {Promise<{ thread: ConversationThread, enrollmentOpportunityId: string | null, personId: string | null }>}
 */
export async function classifyThreadAsNotLead(threadId, input) {
  const body = await api.post(`/rest/conversations/${threadId}/not-a-lead`, {
    notLeadReason: input.notLeadReason,
    notLeadReasonDetail: input.notLeadReasonDetail ?? null,
    personRole: input.personRole ?? null,
  });
  return body?.data ?? body;
}

/**
 * Pessoas de um card que podem receber o contato numa mescla: contato principal, membros da família e alunos.
 * @param {string} enrollmentOpportunityId
 * @returns {Promise<OpportunityPerson[]>}
 */
export async function listOpportunityPeople(enrollmentOpportunityId) {
  const opportunityBody = await api.get(`/rest/enrollmentOpportunities/${enrollmentOpportunityId}`, { depth: 1 });
  const opportunity = readData(opportunityBody, 'enrollmentOpportunity') ?? {};
  const byPersonId = new Map();
  /** @param {any} person @param {OpportunityPerson['role']} role @param {string | null} kinship */
  const add = (person, role, kinship) => {
    if (!person?.id || byPersonId.has(person.id)) return;
    byPersonId.set(person.id, { id: person.id, name: formatFullName(person.name) || person.name || '', role, kinship });
  };
  add(opportunity.pointOfContact, 'POINT_OF_CONTACT', null);
  const [membersBody, studentsBody] = await Promise.all([
    opportunity.householdId
      ? api.get('/rest/householdMembers', { filter: serializeRestFilter({ field: 'householdId', operator: 'eq', value: opportunity.householdId }), depth: 1, limit: 60 })
      : null,
    api.get('/rest/opportunityStudents', { filter: serializeRestFilter({ field: 'enrollmentOpportunityId', operator: 'eq', value: enrollmentOpportunityId }), depth: 1, limit: 60 }),
  ]);
  const members = membersBody ? readData(membersBody, 'householdMembers') : [];
  for (const member of Array.isArray(members) ? members : []) {
    add(member.person, member.householdRole === 'STUDENT' ? 'STUDENT' : 'HOUSEHOLD_MEMBER', member.kinship ?? null);
  }
  const students = readData(studentsBody, 'opportunityStudents');
  for (const student of Array.isArray(students) ? students : []) add(student.student, 'STUDENT', null);
  return [...byPersonId.values()];
}

/**
 * @param {File} file
 * @returns {Promise<{ id: string, fileName: string, mimeType: string, size: number, url?: string }>}
 */
export async function uploadFile(file) {
  const formData = new FormData();
  formData.append('file', file, file.name);
  const body = await api.upload('/rest/files', formData);
  return readData(body, 'file');
}

/**
 * @typedef {{ id: string, displayName: string | null, handle: string, settings: { displayPhoneNumber: string | null, deactivation: any } }} InactiveChannel
 * @returns {Promise<{ channels: Channel[], inactiveChannels: InactiveChannel[], inboundEmailAddress: string | null, metaSignup: { portalOrigin: string } | null }>}
 */
export async function listChannels() {
  const body = await api.get('/rest/channels');
  const data = body?.data ?? body ?? {};
  const channels = Array.isArray(data.channels) ? data.channels : Array.isArray(data) ? data : [];
  const inboundEmailAddress = data.inboundEmailAddress ?? data.emailAddress ?? data.inboundAddress ?? null;
  // contrato 08, seção 8.1: origem do portal para o "Conectar com a Meta" (null quando a plataforma não tem o app da
  // Meta) e os números que a Meta desconectou
  return { channels, inactiveChannels: Array.isArray(data.inactiveChannels) ? data.inactiveChannels : [], inboundEmailAddress, metaSignup: data.metaSignup ?? null };
}

/**
 * @param {{ phoneNumberId: string, wabaId: string, displayPhoneNumber: string, verifiedName: string, accessToken: string }} input
 * @returns {Promise<Channel>}
 */
export async function connectWhatsappChannel(input) {
  const body = await api.post('/rest/channels/whatsapp', input);
  return readData(body, 'channel');
}

/**
 * @param {string} channelId
 */
export async function deleteChannel(channelId) {
  return api.delete(`/rest/channels/${channelId}`);
}
